/**
 * Get Your Answer — canonical question detail page.
 *
 * Renders when a user clicks a canonical (or AI-generated) question card from
 * the library. Visually identical to GetYourAnswerAsk.js (the custom-question
 * page) — both follow the source's question.html design so the funnel feels
 * consistent whether the user typed their own question or picked one from
 * the library.
 *
 * Route: /get-your-answer/:topicId/:slug
 *
 * Spec: see /tmp/schedule_a_meet/KGOB-Ask-a-CPA-offline/question_structure.html
 *       and SEO-013 in /app/docs/SEO_UI_CONSULTATION_LOG.md
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft, ArrowRight, ShieldCheck, FileCheck2, BadgeCheck,
  Compass, ListChecks, Lightbulb,
} from 'lucide-react';
import { buildOutboundUrl, trackCTA } from '@/lib/getYourAnswerApi';
import { fetchQuestion, trackQuestionClick } from '@/lib/getYourAnswerLibraryApi';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// URL slug helpers
const slugToTopicId = (slug) => (slug || '').replace(/-/g, '_');
const topicIdToSlug = (tid) => (tid || '').replace(/_/g, '-');

const URGENCY_LABEL = {
  high:   { text: 'High urgency', tone: 'text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/5' },
  medium: { text: 'Common',       tone: 'text-[#2FA3B5] border-[#2FA3B5]/40 bg-[#2FA3B5]/5' },
  low:    { text: 'Life event',   tone: 'text-[#4C5A70] border-[#4C5A70]/30 bg-[#4C5A70]/5' },
};

const SESSION_LABEL = {
  quick:    { name: 'Quick Question',  min: 30, price: 95 },
  deep_dive:{ name: 'Deep Dive',        min: 60, price: 170 },
  strategy: { name: 'Strategy Session', min: 90, price: 240 },
};

/**
 * Effect-based JSON-LD injector. react-helmet-async v2 doesn't reliably inject
 * <script> children, so we mount/unmount a single <script type="application/ld+json">
 * directly under <head> per question. Googlebot crawls the rendered DOM, so this
 * is the same SEO outcome as Helmet would provide (but actually works).
 */
function QAPageSchemaInjector({ question, topic, topicLabel }) {
  useEffect(() => {
    if (!question || typeof document === 'undefined') return undefined;
    // Remove the build-time baked QAPage script so only one schema is live post-hydration
    const baked = document.getElementById('jsonld-qa-static');
    if (baked && baked.parentNode) baked.parentNode.removeChild(baked);
    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.setAttribute('data-gya-schema', '1');
    tag.textContent = JSON.stringify(buildQAPageSchema(question, topic, topicLabel));
    document.head.appendChild(tag);
    return () => {
      try { document.head.removeChild(tag); } catch (e) { /* already removed */ }
    };
  }, [question, topic, topicLabel]);
  return null;
}

/**
 * Build the QAPage JSON-LD with all fields Google's Q&A rich result spec


 * recommends. The `acceptedAnswer.text` carries the page's editorial framing
 * (topic blurb + the structured "what your CPA will do" prompt) — that's the
 * authoritative answer KGOB publishes on the question. The personalized
 * tax answer comes via the consult, which is the page's CTA.
 *
 * Why this satisfies Google:
 *   - Question.text + datePublished + author → 3 GSC-flagged fields present
 *   - acceptedAnswer with text + author + datePublished → eligible for Q&A rich result
 *   - answerCount: 1 → matches reality (we publish one accepted framing)
 */
function buildQAPageSchema(question, topic, topicLabel) {
  // Google's QAPage schema requires ISO-8601 datetime WITH timezone.
  // The backend serializes `created_at`/`updated_at` in America/New_York
  // (e.g. "2026-06-06T22:13:33.359-04:00"), so we pass it through verbatim
  // rather than re-parsing with `new Date().toISOString()` (which would
  // re-normalize to UTC and lose the ET timezone the client wants).
  let datePublished = question.updated_at || question.created_at;
  if (typeof datePublished !== 'string' || !/^\d{4}-\d{2}-\d{2}T/.test(datePublished)) {
    // Fallback: synthesize "now" in ET. JS doesn't have a native ET formatter,
    // but Intl can produce parts that we re-assemble into an ISO-8601 ET string.
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false, timeZoneName: 'shortOffset',
      }).formatToParts(new Date());
      const get = (t) => ((parts || []).find((p) => p.type === t) || {}).value || '';
      // shortOffset gives "GMT-4" or "GMT-5" — translate to "-04:00" / "-05:00"
      const rawOffset = get('timeZoneName').replace('GMT', '');
      const offset = rawOffset
        ? `${rawOffset[0]}${rawOffset.slice(1).padStart(2, '0')}:00`
        : '-05:00';
      datePublished = `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}${offset}`;
    } catch (e) {
      datePublished = '2026-06-08T00:00:00-04:00';
    }
  }

  const topicBlurb = topic?.blurb
    || `KGOB CPAs covers ${topicLabel} questions every week. Bring your specific situation in for a focused consult and get a clear, plain-English next step.`;

  const answerText = `${topicBlurb} In a focused session, your KGOB advisor reads your exact situation, lays out your options and trade-offs in plain English, and gives you a clear next step you can act on, whether that's with us or on your own. Sessions start at $95 for a 30-minute Quick Question, $170 for a 60-minute Deep Dive, or $240 for a 90-minute Strategy Session.`;

  return {
    '@context': 'https://schema.org',
    '@type': 'QAPage',
    mainEntity: {
      '@type': 'Question',
      name: question.text,
      text: question.text,
      datePublished,
      answerCount: 1,
      author: {
        '@type': 'Organization',
        name: 'Kohari Gonzalez Oneyear & Brown',
        url: 'https://kgob.com',
      },
      acceptedAnswer: {
        '@type': 'Answer',
        text: answerText,
        datePublished,
        upvoteCount: 0,
        url: `https://kgob.com/get-your-answer/${topicIdToSlug(question.topic_id)}/${question.slug}`,
        author: {
          '@type': 'Organization',
          name: 'Kohari Gonzalez Oneyear & Brown',
          url: 'https://kgob.com',
        },
      },
    },
    about: { '@type': 'Thing', name: topicLabel },
  };
}

export default function GetYourAnswerQuestion() {
  const { topicId: topicSlug, slug: questionSlug } = useParams();
  const topicIdFromUrl = slugToTopicId(topicSlug);

  const [topic, setTopic] = useState(null);
  const [allTopics, setAllTopics] = useState([]);
  const [question, setQuestion] = useState(null);
  const [related, setRelated] = useState({ same_canonical: [], same_topic: [], same_crm: [] });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // 1) Topics from local JSON (static metadata — blurb, recommendedLen, etc.)
  useEffect(() => {
    fetch('/data/get-your-answer/topics.json').then((r) => r.json()).then((topics) => {
      setAllTopics(topics);
      const t = (topics || []).find((x) => x.id === topicIdFromUrl);
      if (t) setTopic(t);
    }).catch(() => { /* topic metadata is best-effort */ });
  }, [topicIdFromUrl]);

  // 2) Question itself — API-backed so it works for both manual + AI-generated slugs
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setQuestion(null);

    fetchQuestion(questionSlug).then((q) => {
      if (cancelled) return;
      if (!q) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setQuestion(q);
      setLoading(false);
      trackQuestionClick(q.slug);

      // 3) Related questions (live API; covers all AI-generated phrasings)
      fetch(`${BACKEND}/api/v1/get-your-answer/related/${encodeURIComponent(q.id)}`)
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          setRelated(data || { same_canonical: [], same_topic: [], same_crm: [] });
        })
        .catch(() => { /* related is best-effort */ });
    });

    return () => { cancelled = true; };
  }, [questionSlug]);

  // Explicitly set document.title AND fire a corrected GA page_view when the
  // question data arrives. <Helmet>'s title can lag React state updates on
  // dynamic pages, and the global RouteChangeTracker fires too early (before
  // the question loads), so GA would otherwise record the loading-state title.
  // This effect guarantees GA gets the real question title.
  useEffect(() => {
    if (!question || !question.text) return;
    const fullTitle = `${question.text} | Ask a CPA | KGOB`;
    document.title = fullTitle;
    // Re-fire GA page_view with the corrected title so GA4 reports the actual
    // question (not the loading-state placeholder).
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'page_view', {
          page_path: window.location.pathname + window.location.search,
          page_location: window.location.href,
          page_title: fullTitle,
          send_to: 'G-KYMCLPZQKT',
        });
      } catch (e) { /* analytics never throws */ }
    }
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      try {
        window.dataLayer.push({
          event: 'spa_pageview',
          page_path: window.location.pathname + window.location.search,
          page_location: window.location.href,
          page_title: fullTitle,
        });
      } catch (e) { /* no-op */ }
    }
  }, [question]);

  // Build "related in same topic" list (deduped, 6 items max)
  const relatedSameTopic = useMemo(() => {
    const list = [...(related.same_canonical || []), ...(related.same_topic || [])];
    const seen = new Set();
    const out = [];
    for (const q of list) {
      if (!q || seen.has(q.id) || q.id === question?.id) continue;
      seen.add(q.id);
      out.push(q);
      if (out.length >= 6) break;
    }
    return out;
  }, [related, question]);

  // Related topics: same crm_tag, different topic
  const relatedTopics = useMemo(() => {
    if (!topic || !allTopics.length) return [];
    return allTopics.filter((t) => t.crm_tag === topic.crm_tag && t.id !== topic.id).slice(0, 4);
  }, [topic, allTopics]);

  // User-selected tier (must be declared BEFORE any early-return to obey
  // Rules of Hooks). Defaults to null; resolved to the topic's recommended
  // tier once question data loads. User can override via the picker on the
  // CTA card — no page forces users onto $170/$240 anymore.
  const [selectedTier, setSelectedTier] = useState(null);
  useEffect(() => {
    if (question && !selectedTier) {
      const rec = question.session_type || topic?.recommendedLen || 'quick';
      setSelectedTier(rec in SESSION_LABEL ? rec : 'quick');
    }
  }, [question, topic, selectedTier]);

  if (notFound) {
    return <Navigate to="/get-your-answer/library" replace />;
  }

  // Resolve display values from API doc, falling back to topic metadata
  const displayTopicLabel = question?.topic_label || topic?.label || topicSlug;
  const displayUrgency = question?.urgency || topic?.urgency || 'medium';
  const displaySessionType = question?.session_type || topic?.recommendedLen || 'quick';
  const sessionRec = SESSION_LABEL[displaySessionType] || SESSION_LABEL.quick;
  const urg = URGENCY_LABEL[displayUrgency] || URGENCY_LABEL.medium;

  const effectiveTierKey = selectedTier || displaySessionType;
  const effectiveTier = SESSION_LABEL[effectiveTierKey] || SESSION_LABEL.quick;

  const bookThis = (source) => {
    if (!question) return;
    const tid = question.topic_id || topic?.id || topicIdFromUrl;
    trackCTA(source, { questionSlug: question.slug, topic: tid, session_type: effectiveTierKey });
    window.location.href = buildOutboundUrl(source, {
      topic: tid,
      session_type: effectiveTierKey,
      urgency: question.urgency || topic?.urgency,
      questionSlug: question.slug,
      questionText: question.text,
      campaign: tid,
    });
  };

  return (
    <div className="min-h-screen bg-[#faf9f5]" data-testid="get-your-answer-question-page">
      <Helmet>
        <title>{question ? `${question.text} | Ask a CPA | KGOB` : 'Question · KGOB'}</title>
        <meta
          name="description"
          content={
            question
              ? `${question.text} A licensed NC CPA firm can give you a clear answer in 30 minutes for a flat $95.`
              : 'Bring this question to a licensed CPA firm. Flat $95.'
          }
        />
        {question && (
          <link rel="canonical" href={`https://kgob.com/get-your-answer/${topicIdToSlug(question.topic_id)}/${question.slug}`} />
        )}
      </Helmet>

      {/* QAPage JSON-LD injected directly into <head> via effect — react-helmet-async v2
          does not reliably inject <script> children. Direct injection guarantees the
          schema is present for Googlebot. */}
      <QAPageSchemaInjector question={question} topic={topic} topicLabel={displayTopicLabel} />

      <Header />

      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-10 sm:py-14">
        {loading && (
          <div className="text-center py-32 text-[#4C5A70]" data-testid="qp-loading">Loading…</div>
        )}

        {!loading && question && (
          <>
            {/* ── Breadcrumb ── */}
            <nav className="flex items-center gap-2 text-sm text-[#4C5A70]/70 mb-6" aria-label="Breadcrumb" data-testid="qp-breadcrumb">
              <Link data-testid="get-your-answer-question-link-5" to="/get-your-answer" className="hover:text-[#0B2240] transition-colors">Get Your Answer</Link>
              <span>/</span>
              <Link data-testid="get-your-answer-question-link-4" to="/get-your-answer/library" className="hover:text-[#0B2240] transition-colors">Library</Link>
              <span>/</span>
              <Link data-testid="get-your-answer-question-link-3"
                to="/get-your-answer/library"
                state={{ topic: question.topic_id }}
                className="hover:text-[#0B2240] transition-colors"
              >
                {displayTopicLabel}
              </Link>
            </nav>

            {/* ── Two-column layout: article + sticky CTA ── */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
              {/* ── Article ── */}
              <article>
                <p className="font-['Inter'] text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold mb-3" data-testid="qp-eyebrow">
                  {displayTopicLabel}
                </p>

                <h1
                  className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-[-0.01em] font-semibold text-[#0B2240] mb-5"
                  data-testid="qp-title"
                >
                  &ldquo;{question.text}&rdquo;
                </h1>

                {/* Chips: urgency + session-length recommendation */}
                <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="qp-chips">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold border ${urg.tone}`}>
                    {urg.text}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold border border-[#0B2240]/15 text-[#0B2240]/70">
                    {sessionRec.name} · {sessionRec.min} min · ${sessionRec.price}
                  </span>
                </div>

                {/* Per-question framing — unique 90–130 word context paragraph
                    written for this question specifically. Generated once per
                    canonical (variants inherit via canonical_id lookup on the
                    backend). Falls back to the shared topic blurb if absent so
                    older/unfilled questions never render an empty page. */}
                {question.framing_md ? (
                  <p className="text-[#4C5A70] text-base sm:text-lg leading-relaxed mb-8" data-testid="qp-framing">
                    {question.framing_md}
                  </p>
                ) : topic?.blurb ? (
                  <p className="text-[#4C5A70] text-base sm:text-lg leading-relaxed mb-8" data-testid="qp-framing">
                    {topic.blurb}
                  </p>
                ) : null}

                {/* 3-bullet prompt — from question.html */}
                <div className="mt-2">
                  <p className="text-[#0B2240] font-semibold mb-4">
                    In your <span className="text-[#D4AF37]">{sessionRec.min}-minute session</span>, the KGOB advisor handling it will:
                  </p>
                  <ul className="space-y-4" data-testid="qp-points">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center">
                        <Compass className="w-5 h-5" />
                      </span>
                      <div className="pt-1">
                        <b className="text-[#0B2240]">Read your exact situation</b>
                        <span className="text-[#4C5A70]"> and tell you, in plain English, what&rsquo;s actually going on.</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center">
                        <ListChecks className="w-5 h-5" />
                      </span>
                      <div className="pt-1">
                        <b className="text-[#0B2240]">Lay out your options</b>
                        <span className="text-[#4C5A70]"> and the trade-offs — no jargon, no judgment.</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center">
                        <Lightbulb className="w-5 h-5" />
                      </span>
                      <div className="pt-1">
                        <b className="text-[#0B2240]">Give you a clear next step</b>
                        <span className="text-[#4C5A70]"> you can act on, whether that&rsquo;s with us or on your own.</span>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* ── Related questions in same topic ── */}
                {relatedSameTopic.length > 0 && (
                  <section className="mt-12 pt-10 border-t border-[#E1DFD7]" data-testid="qp-related-section">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="h-px w-8 bg-[#D4AF37]" />
                      <span className="font-['Inter'] text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">
                        More in {displayTopicLabel}
                      </span>
                    </div>
                    <ul className="space-y-2" data-testid="qp-related-list">
                      {(relatedSameTopic || []).map((q) => (
                        <li key={q.id}>
                          <Link
                            to={`/get-your-answer/${topicIdToSlug(q.topic_id)}/${q.slug}`}
                            onClick={() => trackCTA('qp-related-card', { question: q.slug, topic: q.topic_id })}
                            className="group block p-4 rounded-xl bg-white border border-[#E1DFD7] hover:border-[#D4AF37] hover:shadow-sm transition-all"
                            data-testid={`qp-related-${q.id}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <p className="text-[#0B2240] text-sm leading-snug">&ldquo;{q.text}&rdquo;</p>
                              <ArrowRight className="w-3.5 h-3.5 text-[#4C5A70]/40 group-hover:text-[#0B2240] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5">
                      <Link data-testid="get-your-answer-question-link-2"
                        to="/get-your-answer/library"
                        state={{ topic: question.topic_id }}
                        onClick={() => trackCTA('qp-related-see-all', { topic: question.topic_id })}
                        className="inline-flex items-center gap-1.5 text-sm text-[#0B2240] hover:text-[#D4AF37] font-semibold underline-offset-4 hover:underline"
                      >
                        See more in {displayTopicLabel}
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </section>
                )}

                {/* ── Related areas of practice ── */}
                {relatedTopics.length > 0 && (
                  <section className="mt-12 pt-10 border-t border-[#E1DFD7]" data-testid="qp-related-topics-section">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="h-px w-8 bg-[#D4AF37]" />
                      <span className="font-['Inter'] text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">
                        Related areas of practice
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(relatedTopics || []).map((t) => (
                        <Link data-testid="get-your-answer-question-link-1"
                          key={t.id}
                          to="/get-your-answer/library"
                          state={{ topic: t.id }}
                          onClick={() => trackCTA('qp-related-topic', { topic: t.id })}
                          className="group block p-4 rounded-xl bg-white border border-[#E1DFD7] hover:border-[#D4AF37] hover:shadow-sm transition-all"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-semibold text-[#0B2240] text-sm leading-snug">{t.label}</h4>
                            <ArrowRight className="w-3 h-3 text-[#4C5A70]/40 group-hover:text-[#0B2240] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                          </div>
                          {t.canonicalCount && (
                            <p className="text-[11px] text-[#4C5A70]/70 mt-1">{t.canonicalCount} canonical questions</p>
                          )}
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                {/* ── Disclaimer ── */}
                <p className="mt-12 text-[12px] leading-relaxed text-[#4C5A70]/70" data-testid="qp-disclaimer">
                  This page is a prompt to start a conversation, not tax or legal advice, and states
                  no tax-law specifics as fact. A consult session does not by itself create an
                  ongoing engagement. We do not promise specific outcomes or savings. Kohari
                  Gonzalez Oneyear &amp; Brown PLLC — Charlotte, NC.
                </p>

                <Link
                  to="/get-your-answer/library"
                  className="inline-flex items-center gap-2 mt-6 text-sm text-[#4C5A70] hover:text-[#0B2240] transition-colors"
                  data-testid="qp-back-to-library"
                >
                  <ChevronLeft className="w-4 h-4" /> Back to the full library
                </Link>
              </article>

              {/* ── Sidebar CTA card (sticky on lg+) ── */}
              <aside className="lg:sticky lg:top-24 lg:self-start">
                <div className="bg-white rounded-2xl border border-[#E1DFD7] p-6 shadow-sm" data-testid="qp-cta-card">
                  <p className="font-['Inter'] text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold mb-3">
                    Bring this to KGOB
                  </p>

                  {/* Selected tier price (large) — reactive to user's pick */}
                  <div className="flex items-baseline gap-2 mb-3" data-testid="qp-cta-price">
                    <span className="font-['Playfair_Display'] text-4xl font-bold text-[#0B2240]">${effectiveTier.price}</span>
                    <span className="text-sm text-[#4C5A70]">/ {effectiveTier.min}-minute {effectiveTier.name}</span>
                  </div>

                  <p className="text-[11px] text-[#4C5A70]/80 leading-relaxed mb-4">
                    Answered by Kohari Gonzalez Oneyear &amp; Brown — a licensed NC CPA firm.
                  </p>

                  {/* 3-tier selector — recommended tier highlighted but user can pick */}
                  <div className="space-y-2 mb-5" role="radiogroup" aria-label="Session length" data-testid="qp-tier-picker">
                    {(['quick', 'deep_dive', 'strategy']).map((key) => {
                      const tier = SESSION_LABEL[key];
                      const isSelected = effectiveTierKey === key;
                      const isRecommended = displaySessionType === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          onClick={() => setSelectedTier(key)}
                          className={`w-full text-left rounded-lg border px-3 py-2.5 transition-all ${
                            isSelected
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                              : 'border-[#E1DFD7] bg-white hover:border-[#D4AF37]/60'
                          }`}
                          data-testid={`qp-tier-${key}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                                isSelected ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-[#4C5A70]/30'
                              }`} />
                              <span className="text-[13px] font-semibold text-[#0B2240]">{tier.name}</span>
                              {isRecommended && (
                                <span className="text-[9px] tracking-[0.14em] uppercase font-semibold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-1.5 py-0.5 rounded">
                                  Recommended
                                </span>
                              )}
                            </div>
                            <span className="text-[13px] text-[#0B2240] font-semibold whitespace-nowrap">
                              ${tier.price}
                            </span>
                          </div>
                          <p className="text-[11px] text-[#4C5A70]/80 mt-0.5 ml-5.5 pl-[14px]">
                            {tier.min} minutes
                          </p>
                        </button>
                      );
                    })}
                  </div>

                  <Button
                    onClick={() => bookThis('qp-primary-cta')}
                    className="w-full h-12 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-110"
                    data-testid="qp-book-cta"
                  >
                    Book the {effectiveTier.name} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>

                  {/* Trust strip */}
                  <ul className="mt-5 space-y-2 text-[11px] text-[#4C5A70]" data-testid="qp-trust-strip">
                    <li className="flex items-center gap-2">
                      <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                      <span>KGOB — a licensed Charlotte CPA firm</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <BadgeCheck className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                      <span>One flat fee — no commitment</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <FileCheck2 className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                      <span>Documents handled securely</span>
                    </li>
                  </ul>
                </div>
              </aside>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
